interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  ariaLabel?: string;
  className?: string;
}

/**
 * Reusable SVG sparkline. 7-day default, but works with any series length ≥ 2.
 * Renders an area-fill polygon under the polyline. Honey-amber via currentColor.
 */
export function Sparkline({
  data,
  width = 80,
  height = 24,
  ariaLabel,
  className,
}: SparklineProps) {
  if (data.length < 2) {
    return (
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={ariaLabel ?? "Keine Daten"}
        className={className}
      />
    );
  }

  const max = Math.max(...data, 1);
  const stepX = data.length > 1 ? width / (data.length - 1) : width;

  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = height - (v / max) * height;
    return [x, y] as const;
  });

  const linePath = points
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`)
    .join(" ");
  const areaPath = `${linePath} L${width.toFixed(2)},${height} L0,${height} Z`;

  const peak = Math.max(...data);
  const label =
    ariaLabel ?? `${data.length}-Tage-Trend, Höhepunkt ${peak.toLocaleString("de-DE")}`;

  return (
    <svg
      width={width}
      height={height}
      role="img"
      aria-label={label}
      viewBox={`0 0 ${width} ${height}`}
      className={`overflow-visible text-accent ${className ?? ""}`}
    >
      <path d={areaPath} fill="currentColor" fillOpacity={0.12} />
      <path d={linePath} fill="none" stroke="currentColor" strokeWidth={1.25} />
    </svg>
  );
}
